import { useEffect, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import api from "../lib/api";
import errorMessage from "../lib/errors";
import useMeasurementsAutosave from "../lib/useMeasurementsAutosave";
import MeasurementForm from "../components/MeasurementForm";
import MeasurementGuide from "../components/MeasurementGuide";
import MeasurementConfirmModal from "../components/MeasurementConfirmModal";
import AutosaveBadge from "../components/AutosaveBadge";
import { toast } from "sonner";
import { CheckCircle2, Loader2, AlertCircle } from "lucide-react";

export default function MeasurementEntryPage() {
  const { orderId } = useParams();
  const [params] = useSearchParams();
  const token = params.get("token") || "";

  const [loading, setLoading] = useState(true);
  const [order, setOrder] = useState(null);
  const [items, setItems] = useState([]);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const r = await api.get(`/orders/${orderId}/measurement-access?token=${encodeURIComponent(token)}`);
        if (!active) return;
        const o = r.data;
        setOrder(o);
        if (o.measurements_completed) {
          setDone(true);
        } else {
          // Hydrate from the server's measurements_draft if present, else seed empty rows.
          const draft = o.measurements_draft?.items;
          const seed = Array.from({ length: o.quantity }, (_, i) => {
            const d = (Array.isArray(draft) && draft[i]) || {};
            return {
              width: d.width != null ? String(d.width) : "",
              height: d.height != null ? String(d.height) : "",
              unit: "in",
              frame_color: d.frame_color || "White",
              notes: d.notes || "",
              photo_path: d.photo_path || null,
              photo_url: d.photo_url || null,
              petscreen: typeof d.petscreen === "boolean" ? d.petscreen : !!o.petscreen_all,
              spring_side: d.spring_side || null,
              has_springs: typeof d.has_springs === "boolean" ? d.has_springs : true,
              has_pull_tabs: typeof d.has_pull_tabs === "boolean" ? d.has_pull_tabs : true,
              handle_side: d.handle_side || null,
            };
          });
          setItems(seed);
        }
      } catch (e) {
        setError(errorMessage(e, "Could not load order"));
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [orderId, token]);

  const { saving: autosaving, savedAt } = useMeasurementsAutosave({
    orderId,
    token,
    items,
    enabled: !loading && !error && !done && !!order,
  });

  const valid = items.length > 0 && items.every((it) =>
    Number(it.width) > 0 && Number(it.height) > 0 && it.frame_color
  );

  const submit = async () => {
    if (!valid || saving) return;
    setSaving(true);
    try {
      await api.post(
        `/orders/${orderId}/measurements?token=${encodeURIComponent(token)}`,
        {
          items: items.map((it) => ({
            width: Number(it.width),
            height: Number(it.height),
            unit: "in",
            frame_color: it.frame_color,
            notes: it.notes || "",
            photo_path: it.photo_path || null,
            petscreen: !!it.petscreen,
            spring_side: it.spring_side || null,
            has_springs: it.has_springs !== false,
            has_pull_tabs: it.has_pull_tabs !== false,
            handle_side: it.handle_side || null,
          })),
        }
      );
      setDone(true);
      setConfirmOpen(false);
      toast.success("Measurements received!");
    } catch (e) {
      toast.error(errorMessage(e, "Failed to submit"));
    } finally {
      setSaving(false);
    }
  };

  const requestSubmit = () => {
    if (!valid || saving) return;
    setConfirmOpen(true);
  };

  const isWindow = order?.product_slug === "window-screen";

  const addProMeasurement = async () => {
    try {
      const nextUpsells = { ...(order?.upsells || {}), pro_measurement: true };
      await api.post(`/orders/${orderId}/upsells`, { upsells: nextUpsells });
      setDone(true);
      toast.success("Pro Measurement added — we'll be in touch to schedule.");
    } catch (e) {
      toast.error(errorMessage(e, "Couldn't add Pro Measurement"));
    }
  };

  return (
    <div className="min-h-screen flex flex-col" style={{ background: "#f7faf6" }}>
      <header className="px-5 py-4 border-b bg-white sticky top-0 z-20">
        <div className="max-w-md mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img
              src="https://customer-assets.emergentagent.com/job_tech-dispatch-31/artifacts/a6ta9f48_SCREENZZ-LOGO-RGB_TextOnly.png"
              alt="ScreenzZ" className="h-8" />
          </Link>
          <span className="step-pill">Measurements</span>
        </div>
      </header>

      <main className="flex-1 px-5 py-6">
        <div className="max-w-md mx-auto">
          {loading && (
            <div className="text-center py-12" data-testid="measure-loading">
              <Loader2 className="w-6 h-6 mx-auto animate-spin brand-text" />
              <p className="muted-text text-sm mt-2">Loading your order…</p>
            </div>
          )}

          {error && !loading && (
            <div className="option-card text-center" data-testid="measure-error">
              <AlertCircle className="w-8 h-8 mx-auto text-red-500 mb-2" />
              <p className="font-semibold text-[var(--brand-text)]">Link not valid</p>
              <p className="text-sm muted-text mt-1">{error}</p>
              <Link to="/" className="cta-btn mt-4 inline-block" style={{ textDecoration: "none" }}>
                Back home
              </Link>
            </div>
          )}

          {!loading && !error && done && (
            <div className="option-card text-center" data-testid="measure-done">
              <CheckCircle2 className="w-12 h-12 mx-auto brand-text mb-2" />
              <h1 className="text-2xl font-bold text-[var(--brand-text)]">Measurements received</h1>
              <p className="muted-text text-sm mt-2">
                Thanks! We&apos;re building order <strong>{order?.order_number}</strong>.
              </p>
            </div>
          )}

          {!loading && !error && !done && order && (
            <>
              <div className="step-pill mb-3" style={{ background: "#dcfce7", color: "#166534" }}>
                Order {order.order_number} · paid
              </div>
              <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight mb-2 text-[var(--brand-text)]">
                Enter measurements
              </h1>
              <p className="muted-text mb-3">
                {order.quantity} × {order.product_name}. We&apos;ll start production as soon as you submit.
              </p>

              <AutosaveBadge saving={autosaving} savedAt={savedAt} />

              <MeasurementGuide
                productType={isWindow ? "window" : "sliding"}
                onAddProMeasurement={addProMeasurement}
              />

              <MeasurementForm
                items={items}
                onChange={setItems}
                petscreenAll={!!order.petscreen_all}
                showSpringAxis={isWindow}
              />

              <button
                data-testid="submit-measurements-btn"
                className="cta-btn mt-8"
                disabled={!valid || saving}
                onClick={requestSubmit}
              >
                {saving
                  ? <span className="inline-flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Saving</span>
                  : "Submit measurements"}
              </button>

              <MeasurementConfirmModal
                open={confirmOpen}
                onClose={() => setConfirmOpen(false)}
                onConfirm={submit}
                items={items}
                productName={order?.product_name || "screen"}
                saving={saving}
              />
            </>
          )}
        </div>
      </main>

      <footer className="py-6 text-center text-xs muted-text">
        <p>© {new Date().getFullYear()} ScreenzZ</p>
        <div className="flex items-center justify-center gap-4 mt-1 flex-wrap">
          <Link to="/privacy-policy"
            className="text-[11px] muted-text hover:brand-text underline-offset-2 hover:underline">
            Privacy
          </Link>
          <Link to="/terms"
            className="text-[11px] muted-text hover:brand-text underline-offset-2 hover:underline">
            Terms
          </Link>
          <a href="tel:8774000889"
            className="text-[11px] muted-text hover:brand-text underline-offset-2 hover:underline">
            Contact
          </a>
          <Link
            to="/admin/login"
            data-testid="footer-admin-link"
            className="text-[11px] muted-text hover:brand-text underline-offset-2 hover:underline"
          >
            Admin
          </Link>
        </div>
      </footer>
    </div>
  );
}
